<script>
import { mapState } from 'vuex';
import { showSpace } from '@intercom/messenger-js-sdk';

import AccountControlPanel from '@/components/account/AccountControlPanel.vue';
import AccountManage from '@/components/account/AccountManage.vue';
import AccountEdit from '@/components/account/AccountEdit.vue';
import AccountNew from '@/components/account/AccountNew.vue';
import AccountPopover from '@/components/account/AccountPopover.vue';

export default {
  name: 'FormHeader',
  components: {
    AccountControlPanel,
    AccountManage,
    AccountEdit,
    AccountNew,
    AccountPopover
  },
  data() {
    return {
      openPanelString: null, //'new' | 'edit' | 'manage' | 'new'
      shouldShowBackButton: true,
      activeTabString: '',
      showOverlay: false,
      componentKey: 0
    };
  },

  computed: {
    ...mapState('auth', ['currentUser'])
  },

  watch: {
    '$route.query.accountPanel': {
      handler() {
        this.openPanelFromQuery();
      },
      immediate: true
    }
  },

  methods: {
    openHelp() {
      showSpace('help');
    },
    openPanel(panelString, activeTabString) {
      const isValidPanelString = ['manage', 'edit', 'new'].includes(panelString);
      if (!isValidPanelString) {
        throw new Error('invalid panel string');
      }
      this.openPanelString = panelString;
      if (activeTabString) {
        this.activeTabString = activeTabString;
      }

      this.$emit('toggle-panel', 'opened');
      this.$root.$emit('bv::hide::popover');
    },
    openManagerPanel() {
      this.openPanel('manage');
      this.activeTabString = 'orgInfo';
    },
    closePanel() {
      this.openPanelString = null;
      this.shouldShowBackButton = true;
      this.$emit('toggle-panel', 'closed');
      if (this.$route.query.accountPanel) {
        const query = { ...this.$route.query };
        delete query.accountPanel;
        this.$router.replace({ query });
      }
    },
    openAccountEdit() {
      this.openPanelString = 'edit';
      this.shouldShowBackButton = true;
    },
    openAccountNew() {
      this.openPanelString = 'new';
    },
    openPanelFromQuery() {
      if (this.$route.query.accountPanel !== 'edit') {
        return;
      }

      this.shouldShowBackButton = false;
      this.openPanel('edit');
    }
  }
};
</script>

<template>
  <header class="app-header">
    <div :class="{ overlay: openPanelString }"></div>
    <nav class="nav">
      <!-- Mobile hamburger menu -->
      <button class="hidden md:hidden items-center justify-center w-6 h-6 bg-transparent border-0 cursor-pointer p-0 mr-auto transition-colors duration-300 hamburger-menu" @click="$emit('toggleMobileMenu')">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" class="w-full h-full transition-colors duration-300">
          <path
            d="M96 160C96 142.3 110.3 128 128 128L512 128C529.7 128 544 142.3 544 160C544 177.7 529.7 192 512 192L128 192C110.3 192 96 177.7 96 160zM96 320C96 302.3 110.3 288 128 288L512 288C529.7 288 544 302.3 544 320C544 337.7 529.7 352 512 352L128 352C110.3 352 96 337.7 96 320zM544 480C544 497.7 529.7 512 512 512L128 512C110.3 512 96 497.7 96 480C96 462.3 110.3 448 128 448L512 448C529.7 448 544 462.3 544 480z"
            class="hamburger-path"
          />
        </svg>
      </button>

      <transition name="account-panel-slide">
        <account-control-panel v-if="openPanelString === 'manage'" @close-panel="closePanel" action-name="Organization Manager">
          <account-manage
            @navigate-panel="openPanel($event)"
            @account-edit="openAccountEdit"
            @remount-component="componentKey++"
            :key="componentKey"
            :active-tab-prop="activeTabString"
          />
        </account-control-panel>
        <account-control-panel v-if="openPanelString === 'edit'" @close-panel="closePanel" action-name="Edit User">
          <account-edit @navigate-panel="openPanel($event, 'userList')" :user="currentUser" :back-button="shouldShowBackButton" />
        </account-control-panel>

        <account-control-panel v-if="openPanelString === 'new'" @close-panel="closePanel" action-name="Invite New User">
          <account-new @navigate-panel="openPanel($event, 'userList')" />
        </account-control-panel>
      </transition>
      <div class="header-buttons" id="header-buttons">
        <button type="button" class="app-icon-button tooltip-bottom tooltip-light" data-tooltip="Support" aria-label="Support" @click="openHelp">
          <app-icon type="font" name="help-circle" />
        </button>

        <button class="app-icon-button tooltip-bottom tooltip-light" data-tooltip="Notifications">
          <app-icon type="font" name="notifications" />
        </button>

        <button v-popover:account class="app-icon-button tooltip-bottom tooltip-light" data-tooltip="Account">
          <app-icon type="font" name="person" />
        </button>
        <account-popover @open-manager-panel="openManagerPanel" @open-edit-panel="openPanel('edit')" />
      </div>
    </nav>
  </header>
</template>

<style lang="scss" scoped>
.app-header {
  padding: 16px 58px;

  .overlay {
    position: fixed;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    background: #1e2240;
    opacity: 0.7;
    z-index: 10;
    transition: 0.15s ease-out;
  }

  .nav {
    display: flex;
    align-items: center;
    justify-content: flex-end;

    .hamburger-path {
      fill: var(--primary-color);
      transition: fill 0.3s ease;
    }

    .hamburger-menu:hover .hamburger-path {
      fill: var(--blue-dark);
    }

    .header-buttons {
      display: flex;
      align-items: center;

      &:hover .app-icon-button:not(:hover),
      .app-icon-button:focus {
        &::before,
        &::after {
          visibility: hidden;
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .app-header .nav .hamburger-menu {
    display: flex !important;
  }
}

.account-panel-slide-enter-active,
.account-panel-slide-leave-active {
  transition: transform 0.2s ease;
}

.account-panel-slide-enter,
.account-panel-slide-leave-to {
  transform: translateX(100%);
  transition: all 150ms ease-in 0s;
}
</style>
